import React, { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { env } from 'src/lib/env'
import {
  useFeatureFlagsQuery,
  useSetFeatureFlagOverrideMutation,
  type FeatureFlagFieldsFragment,
} from './featureFlags.generated'

const featureFlagKeys = ['featureFlagDashboardPriorityFilters', 'featureFlagAdvancedDashboardDisabled'] as const
export type FeatureFlagKey = (typeof featureFlagKeys)[number]

export type ToggleableFeatureFlagLabels = Partial<Record<FeatureFlagKey, string>>

/**
 * The flags the Experiments menu may offer, with the label it shows for each. A flag the server
 * reports as toggleable stays out of the menu until it is listed here, so an empty list hides the
 * menu in both app bars.
 *
 * TODO: make it possible to opt out from feature flags via firebase remote configs
 */
const toggleableFeatureFlagLabels: ToggleableFeatureFlagLabels = {}

type FeatureFlags = Partial<Record<FeatureFlagKey, boolean>>

export type ToggleableFeatureFlag = { key: FeatureFlagKey; label: string; enabled: boolean }

type FeatureFlagContextValue = {
  flags: FeatureFlags
  loading: boolean
  toggleableFlags: ToggleableFeatureFlag[]
  setFeatureFlag: (key: FeatureFlagKey, enabled: boolean) => Promise<unknown>
}

// Local-only overrides: uncomment a flag to force its value while developing.
// Ignored outside NODE_ENV=development, so a forgotten override can't reach staging or production.
const featureFlagOverrides: FeatureFlags = {
  // featureFlagDashboardPriorityFilters: false,
}

const isFeatureFlagKey = (key: string): key is FeatureFlagKey => (featureFlagKeys as readonly string[]).includes(key)

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  loading: false,
  toggleableFlags: [],
  setFeatureFlag: () => Promise.resolve(),
})

type ProviderProps = {
  children: ReactNode
  /** Tests pass their own list; the app uses the one above. */
  toggleableFlagLabels?: ToggleableFeatureFlagLabels
}

export function FeatureFlagProvider({ children, toggleableFlagLabels = toggleableFeatureFlagLabels }: ProviderProps) {
  const { data, loading } = useFeatureFlagsQuery()
  const [setFeatureFlagOverride] = useSetFeatureFlagOverrideMutation()

  // Reload instead of updating the cache so no in-memory state built under the old flag value
  // survives into the new one.
  const setFeatureFlag = useCallback(
    async (key: FeatureFlagKey, enabled: boolean) => {
      await setFeatureFlagOverride({ variables: { key, enabled } })
      window.location.reload()
    },
    [setFeatureFlagOverride]
  )

  const value = useMemo(() => {
    const serverFlags: FeatureFlagFieldsFragment[] = data?.featureFlags ?? []
    return {
      flags: {
        ...Object.fromEntries(serverFlags.map((flag) => [flag.key, flag.enabled])),
        ...(env.NODE_ENV === 'development' ? featureFlagOverrides : {}),
      },
      loading,
      toggleableFlags: serverFlags.flatMap((flag) => {
        if (!flag.toggleable || !isFeatureFlagKey(flag.key)) {
          return []
        }
        const label = toggleableFlagLabels[flag.key]
        return label ? [{ key: flag.key, label, enabled: flag.enabled }] : []
      }),
      setFeatureFlag,
    }
  }, [data?.featureFlags, loading, setFeatureFlag, toggleableFlagLabels])

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>
}

export function useFeatureFlag(key: FeatureFlagKey) {
  return useContext(FeatureFlagContext).flags[key] ?? false
}

export function useFeatureFlagsLoading() {
  return useContext(FeatureFlagContext).loading
}

/** The flags the signed-in coach may switch on and off, and the way to do so. */
export function useToggleableFeatureFlags() {
  const { toggleableFlags, setFeatureFlag } = useContext(FeatureFlagContext)
  return { toggleableFlags, setFeatureFlag }
}
